// apps/web/src/pages/vendas/detalhe/nfs-vinculadas-aba.tsx — Fase 12, Plano 11 (GOV-02, D-39; UI-SPEC § NFs vinculadas).
// Aba "NFs vinculadas": notas ligadas ao PV (pré-alocação ou recebimento). Carregada só ao abrir a aba (o conteúdo da
// aba é montado sob demanda). Chave de acesso encurtada com botão visível "Copiar chave"; completa na conferência da NF.
import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, RefreshCw } from 'lucide-react';
import type { NfVinculadaPv } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { encurtarChaveNfe } from '../../../lib/vendas/chave-nfe';
import { formatarData, formatarQuantidade } from '../../../lib/vendas/formatacao';

interface NfsVinculadasAbaProps {
  readonly pedidoId: string;
  /** pv:ajustar: habilita o botão de vínculo retroativo no estado vazio. */
  readonly podeVincularExistente: boolean;
  readonly onVincularExistente: () => void;
  /** Muda quando o PV é recarregado (ex.: após vincular lote) para reler as notas. */
  readonly versaoPv: number;
}

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

export function NfsVinculadasAba({ pedidoId, podeVincularExistente, onVincularExistente, versaoPv }: NfsVinculadasAbaProps) {
  const [nfs, setNfs] = useState<NfVinculadaPv[] | null>(null);
  const [erro, setErro] = useState(false);
  const [copiada, setCopiada] = useState<string | null>(null);

  const carregar = useCallback(
    async (sinal: { vivo: boolean }) => {
      setErro(false);
      try {
        const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/nfs`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as NfVinculadaPv[];
        if (sinal.vivo) setNfs(dados);
      } catch {
        if (sinal.vivo) setErro(true);
      }
    },
    [pedidoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, versaoPv]);

  useEffect(() => {
    if (!copiada) return;
    const timer = window.setTimeout(() => setCopiada(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copiada]);

  async function copiar(chave: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(chave);
      setCopiada(chave);
    } catch {
      // Sem permissão de área de transferência: a chave completa continua na conferência da NF.
    }
  }

  if (erro) {
    return (
      <Card>
        <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-rose-700">Não foi possível carregar as NFs vinculadas. Verifique a conexão e tente novamente.</p>
          <Button type="button" variant="outline" onClick={() => void carregar({ vivo: true })}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  if (nfs === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando NFs vinculadas">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (nfs.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm font-medium">Nenhuma NF vinculada a este PV ainda.</p>
          <p className="max-w-xl text-sm text-muted-foreground">
            Use &quot;Criar PV desta nota&quot; na conferência de uma NF, ou vincule um lote já recebido.
          </p>
          {podeVincularExistente && (
            <Button type="button" variant="outline" onClick={onVincularExistente}>
              Vincular lote ou remessa existente
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden py-0 gap-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th scope="col" className={CABECALHO}>NF</th>
              <th scope="col" className={CABECALHO}>Chave</th>
              <th scope="col" className={CABECALHO}>Emissão</th>
              <th scope="col" className={`${CABECALHO} text-right`}>Itens vinculados</th>
              <th scope="col" className={`${CABECALHO} text-right`}>Recebido</th>
              <th scope="col" className={CABECALHO}>
                <span className="sr-only">Ação</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {nfs.map((nf) => (
              <tr key={nf.documentoFiscalId} className="border-t align-middle">
                <td className="px-3 py-2 font-medium tabular-nums">{nf.numero ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1">
                    <span className="font-mono text-xs tabular-nums" title={nf.chave ?? undefined}>
                      {encurtarChaveNfe(nf.chave)}
                    </span>
                    {nf.chave && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Copiar chave de acesso"
                        className="min-h-11 gap-1 px-2 sm:min-h-8"
                        onClick={() => void copiar(nf.chave!)}
                      >
                        {copiada === nf.chave ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                        <span className="text-xs">{copiada === nf.chave ? 'Copiada' : 'Copiar chave'}</span>
                      </Button>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2 tabular-nums">{formatarData(nf.emissao)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{nf.itensVinculados}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(nf.recebido, 'un')}</td>
                <td className="px-3 py-2 text-right">
                  <Button asChild variant="outline" size="sm">
                    <a href={`/remessas?nota=${encodeURIComponent(nf.documentoFiscalId)}`}>Abrir conferência</a>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
